import { apiClient } from './api'
import type {
  Account,
  AccountCreateDto,
  AccountUserListResponse,
  AccountInvite,
  AccountInviteListResponse
} from '@/types/account.types'

export type { AccountCreateDto }

export interface AccountListResponse {
  accounts: Account[]
  total: number
}

const mapAccount = (account: any): Account => ({
  id: account.id,
  name: account.name,
  description: account.description,
  accountType: account.account_type,
  country: account.country,
  priceBook: account.price_book,
  webhookUrl: account.webhook_url,
  isActive: account.is_active ?? true,
  isUniversal: account.is_universal ?? false,
  deviceCount: account.device_count || 0,
  createdAt: account.created_at,
  updatedAt: account.updated_at
})

export const accountService = {
  getAccounts: async (): Promise<AccountListResponse> => {
    const { data } = await apiClient.get('/accounts')
    return {
      accounts: (data.accounts || []).map(mapAccount),
      total: data.total || 0
    }
  },

  getAccount: async (accountId: string): Promise<Account> => {
    const { data } = await apiClient.get(`/accounts/${accountId}`)
    return mapAccount(data)
  },

  createAccount: async (account: AccountCreateDto): Promise<Account> => {
    const { data } = await apiClient.post('/accounts', account)
    return mapAccount(data)
  },

  updateAccount: async (accountId: string, account: Partial<AccountCreateDto>): Promise<Account> => {
    const { data } = await apiClient.put(`/accounts/${accountId}`, account)
    return mapAccount(data)
  },

  deleteAccount: async (accountId: string): Promise<void> => {
    await apiClient.delete(`/accounts/${accountId}`)
  },

  archiveAccount: async (accountId: string): Promise<void> => {
    await apiClient.post(`/accounts/${accountId}/archive`)
  },

  restoreAccount: async (accountId: string): Promise<void> => {
    await apiClient.post(`/accounts/${accountId}/restore`)
  },

  // Sub-accounts
  getSubAccounts: async (accountId: string): Promise<Account[]> => {
    const { data } = await apiClient.get(`/accounts/${accountId}/sub-accounts`)
    return data.map(mapAccount)
  },

  linkSubAccount: async (accountId: string, childAccountId: string): Promise<void> => {
    await apiClient.post(`/accounts/${accountId}/sub-accounts/${childAccountId}`)
  },

  unlinkSubAccount: async (accountId: string, childAccountId: string): Promise<void> => {
    await apiClient.delete(`/accounts/${accountId}/sub-accounts/${childAccountId}`)
  },

  // Users
  getAccountUsers: async (accountId: string): Promise<AccountUserListResponse> => {
    const { data } = await apiClient.get(`/accounts/${accountId}/users`)
    return {
      users: data.users.map((user: any) => ({
        id: user.id,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        displayName: user.display_name,
        isSuperUser: user.is_super_user ?? false,
        isAdmin: user.is_admin ?? false,
        isDefault: user.is_default ?? false,
        lastLogin: user.last_login
      })),
      total: data.total
    }
  },

  linkUser: async (accountId: string, userId: string, options?: { isAdmin?: boolean; isDefault?: boolean }): Promise<void> => {
    await apiClient.post(`/accounts/${accountId}/users/${userId}`, {
      is_default: options?.isDefault ?? false,
      is_admin: options?.isAdmin ?? false
    })
  },

  unlinkUser: async (accountId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/accounts/${accountId}/users/${userId}`)
  },

  toggleUserAdmin: async (accountId: string, userId: string, isAdmin: boolean): Promise<void> => {
    await apiClient.put(`/accounts/${accountId}/users/${userId}/admin`, {
      is_admin: isAdmin
    })
  },

  // Invitations
  getInvitations: async (accountId: string): Promise<AccountInviteListResponse> => {
    const { data } = await apiClient.get(`/accounts/${accountId}/invites`)
    return {
      invites: data.invites.map((invite: any) => ({
        id: invite.id,
        accountId: invite.account_id,
        email: invite.email,
        inviterUserId: invite.inviter_user_id,
        inviterName: invite.inviter_name,
        accepted: invite.accepted,
        createdAt: invite.created_at,
        acceptedAt: invite.accepted_at,
        isExpired: invite.is_expired,
        userLinked: invite.user_linked
      })),
      total: data.total
    }
  },

  inviteUser: async (accountId: string, email: string): Promise<AccountInvite> => {
    const { data } = await apiClient.post(`/accounts/${accountId}/invites`, { email })
    return {
      id: data.id,
      accountId: data.account_id,
      email: data.email,
      inviterUserId: data.inviter_user_id,
      inviterName: data.inviter_name,
      accepted: data.accepted,
      createdAt: data.created_at,
      acceptedAt: data.accepted_at,
      isExpired: data.is_expired,
      userLinked: data.user_linked
    }
  },

  resendInvitation: async (accountId: string, inviteId: string): Promise<void> => {
    await apiClient.post(`/accounts/${accountId}/invites/${inviteId}/resend`)
  },

  deleteInvitation: async (accountId: string, inviteId: string): Promise<void> => {
    await apiClient.delete(`/accounts/${accountId}/invites/${inviteId}`)
  }
}
